import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Separator } from './ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Progress } from './ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Leaf,
  Users,
  BarChart3,
  Settings,
  Bell,
  Search,
  Plus,
  MoreHorizontal,
  TrendingUp,
  TrendingDown,
  Eye,
  Edit,
  Trash2,
  Download,
  Upload,
  Filter,
  Mail,
  MapPin,
  Shield,
  AlertTriangle,
  Activity,
  Server,
  LogOut,
} from 'lucide-react';
import AdminSettings from './AdminSettings';

// Sample data
const dashboardStats = [
  {
    title: "Total Users",
    value: "2,847",
    change: "+12.5%",
    trend: "up",
    icon: Users,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
  },
  {
    title: "Active Plants",
    value: "15,234",
    change: "+8.2%",
    trend: "up",
    icon: Leaf,
    color: "text-emerald-600",
    bgColor: "bg-emerald-50",
  },
  {
    title: "Monthly Revenue",
    value: "€12,450",
    change: "+15.3%",
    trend: "up",
    icon: BarChart3,
    color: "text-green-600",
    bgColor: "bg-green-50",
  },
  {
    title: "Support Tickets",
    value: "23",
    change: "-5.1%",
    trend: "down",
    icon: AlertTriangle,
    color: "text-amber-600",
    bgColor: "bg-amber-50",
  },
];

const recentUsers = [
  {
    id: "1",
    name: "Ona Petraitienė",
    email: "ona.p@example.com",
    location: "Vilnius",
    plants: 12,
    status: "active",
    joinDate: "2024-01-15",
    avatar: "/placeholder.svg?height=32&width=32",
  },
  {
    id: "2",
    name: "Petras Kazlauskas",
    email: "petras.k@example.com",
    location: "Kaunas",
    plants: 8,
    status: "active",
    joinDate: "2024-01-14",
    avatar: "/placeholder.svg?height=32&width=32",
  },
  {
    id: "3",
    name: "Rasa Jonauskienė",
    email: "rasa.j@example.com",
    location: "Klaipėda",
    plants: 15,
    status: "inactive",
    joinDate: "2024-01-13",
    avatar: "/placeholder.svg?height=32&width=32",
  },
  {
    id: "4",
    name: "Jonas Vilkas",
    email: "jonas.v@example.com",
    location: "Šiauliai",
    plants: 6,
    status: "active",
    joinDate: "2024-01-12",
    avatar: "/placeholder.svg?height=32&width=32",
  },
  {
    id: "5",
    name: "Marija Žukienė",
    email: "marija.z@example.com",
    location: "Panevėžys",
    plants: 20,
    status: "premium",
    joinDate: "2024-01-11",
    avatar: "/placeholder.svg?height=32&width=32",
  },
];

const systemMetrics = [
  { name: "Server CPU", value: 45, status: "good" },
  { name: "Memory Usage", value: 67, status: "warning" },
  { name: "Database Load", value: 23, status: "good" },
  { name: "API Response", value: 89, status: "critical" },
];

const AdminDashboard: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "inactive":
        return "bg-gray-100 text-gray-800";
      case "premium":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };



  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo & Title */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-600 text-white shadow-md">
                  <Leaf className="h-5 w-5" />
                </div>
                <div className="flex flex-col">
                  <span className="text-lg font-bold text-gray-900">Kiemo Draugas</span>
                  <span className="text-xs text-gray-500">Administrator Panel</span>
                </div>
              </div>
              <Separator orientation="vertical" className="h-8" />
              <h1 className="text-xl font-semibold text-gray-900">Dashboard</h1>
            </div>

            {/* Header Actions */}
            <div className="flex items-center gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search users, plants..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64 bg-gray-50 border-gray-200"
                />
              </div>

              {/* Notifications */}
              <Button variant="ghost" size="sm" className="relative">
                <Bell className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full text-xs"></span>
              </Button>

              {/* Profile Menu */}
              <div className="relative dropdown-container" ref={dropdownRef}>
                <Button 
                  variant="ghost" 
                  className="relative h-10 w-10 rounded-full"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src="/placeholder.svg?height=40&width=40" alt="Admin" />
                    <AvatarFallback>AD</AvatarFallback>
                  </Avatar>
                </Button>
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 user-dropdown">
                    <div className="py-2">
                      <div className="px-4 py-2">
                        <p className="text-sm font-medium">Admin User</p>
                        <p className="text-xs text-gray-500">admin@kiemopriežiūra.lt</p>
                      </div>
                      <div className="border-t border-gray-200 my-1"></div>
                      <button 
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center"
                        onClick={() => {
                          setSelectedTab("settings");
                          setIsDropdownOpen(false);
                        }}
                      >
                        <Settings className="mr-2 h-4 w-4" />
                        <span>Settings</span>
                      </button>
                      <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center">
                        <Shield className="mr-2 h-4 w-4" />
                        <span>Security</span>
                      </button>
                      <div className="border-t border-gray-200 my-1"></div>
                      <button 
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center text-red-600"
                        onClick={() => {
                          handleLogout();
                          setIsDropdownOpen(false);
                        }}
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        <span>Log out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-gray-200 min-h-screen">
          <nav className="p-4 space-y-2">
            <div className="space-y-1">
              <Button
                variant={selectedTab === "overview" ? "secondary" : "ghost"}
                className="w-full justify-start gap-2"
                onClick={() => setSelectedTab("overview")}
              >
                <BarChart3 className="w-4 h-4" />
                Overview
              </Button>
              <Button
                variant={selectedTab === "users" ? "secondary" : "ghost"}
                className="w-full justify-start gap-2"
                onClick={() => setSelectedTab("users")}
              >
                <Users className="w-4 h-4" />
                Users
              </Button>
              <Button
                variant={selectedTab === "plants" ? "secondary" : "ghost"}
                className="w-full justify-start gap-2"
                onClick={() => setSelectedTab("plants")}
              >
                <Leaf className="w-4 h-4" />
                Plants
              </Button>
              <Button
                variant={selectedTab === "analytics" ? "secondary" : "ghost"}
                className="w-full justify-start gap-2"
                onClick={() => setSelectedTab("analytics")}
              >
                <Activity className="w-4 h-4" />
                Analytics
              </Button>
              <Button
                variant={selectedTab === "system" ? "secondary" : "ghost"}
                className="w-full justify-start gap-2"
                onClick={() => setSelectedTab("system")}
              >
                <Server className="w-4 h-4" />
                System
              </Button>
              <Button
                variant={selectedTab === "settings" ? "secondary" : "ghost"}
                className="w-full justify-start gap-2"
                onClick={() => setSelectedTab("settings")}
              >
                <Settings className="w-4 h-4" />
                Settings
              </Button>
            </div>

            <Separator className="my-4" />

            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2">Quick Actions</p>
              <Button variant="ghost" className="w-full justify-start gap-2 text-sm">
                <Plus className="w-4 h-4" />
                Add User
              </Button>
              <Button variant="ghost" className="w-full justify-start gap-2 text-sm">
                <Upload className="w-4 h-4" />
                Import Data
              </Button>
              <Button variant="ghost" className="w-full justify-start gap-2 text-sm">
                <Download className="w-4 h-4" />
                Export Report
              </Button>
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          {selectedTab === "overview" && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {dashboardStats.map((stat, index) => (
                  <Card key={index} className="border-0 shadow-sm">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                          <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                          <div className="flex items-center mt-2">
                            {stat.trend === "up" ? (
                              <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                            ) : (
                              <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                            )}
                            <span
                              className={`text-sm font-medium ${
                                stat.trend === "up" ? "text-green-600" : "text-red-600"
                              }`}
                            >
                              {stat.change}
                            </span>
                            <span className="text-sm text-gray-500 ml-1">vs last month</span>
                          </div>
                        </div>
                        <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                          <stat.icon className={`w-6 h-6 ${stat.color}`} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Charts and Recent Activity */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* System Health */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="w-5 h-5" />
                      System Health
                    </CardTitle>
                    <CardDescription>Real-time system performance metrics</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {systemMetrics.map((metric, index) => (
                      <div key={index} className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">{metric.name}</span>
                          <span className="text-gray-500">{metric.value}%</span>
                        </div>
                        <Progress value={metric.value} className="h-2" />
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Recent Users */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Recent Users
                    </CardTitle>
                    <CardDescription>Latest user registrations</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {recentUsers.slice(0, 4).map((user) => (
                        <div key={user.id} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={user.avatar || "/placeholder.svg"} alt={user.name} />
                              <AvatarFallback>
                                {user.name
                                  .split(" ")
                                  .map((n) => n[0])
                                  .join("")}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-sm font-medium">{user.name}</p>
                              <p className="text-xs text-gray-500">{user.location}</p>
                            </div>
                          </div>
                          <Badge className={getStatusColor(user.status)}>{user.status}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {selectedTab === "users" && (
            <div className="space-y-6">
              {/* Users Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Users Management</h2>
                  <p className="text-gray-600">Manage user accounts and permissions</p>
                </div>
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="sm">
                    <Filter className="w-4 h-4 mr-2" />
                    Filter
                  </Button>
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add User
                  </Button>
                </div>
              </div>

              {/* Users Table */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Plants</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Join Date</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recentUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={user.avatar || "/placeholder.svg"} alt={user.name} />
                                <AvatarFallback>
                                  {user.name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{user.name}</p>
                                <p className="text-sm text-gray-500">{user.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              {user.location}
                            </div>
                          </TableCell>
                          <TableCell>{user.plants}</TableCell>
                          <TableCell>
                            <Badge className={getStatusColor(user.status)}>{user.status}</Badge>
                          </TableCell>
                          <TableCell>{user.joinDate}</TableCell>
                          <TableCell className="text-right">
                            <div className="relative">
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                                <div className="py-1">
                                  <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center">
                                    <Eye className="mr-2 h-4 w-4" />
                                    View Details
                                  </button>
                                  <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center">
                                    <Edit className="mr-2 h-4 w-4" />
                                    Edit User
                                  </button>
                                  <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center">
                                    <Mail className="mr-2 h-4 w-4" />
                                    Send Message
                                  </button>
                                  <div className="border-t border-gray-200 my-1"></div>
                                  <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center text-red-600">
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete User
                                  </button>
                                </div>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {selectedTab === "settings" && (
            <AdminSettings 
              onSettingChange={(key, value) => {
                console.log(`Setting changed: ${key} = ${value}`);
                // Handle setting changes here if needed
              }}
            />
          )}

          {/* Placeholder for other tabs */}
          {(selectedTab === "plants" || selectedTab === "analytics" || selectedTab === "system") && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Settings className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">{selectedTab.charAt(0).toUpperCase() + selectedTab.slice(1)}</h3>
                <p className="text-gray-500">This section is under development</p>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard; 